import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';

export default function MarketTicker() {
  const [tickerData, setTickerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchTicker = async () => {
      try {
        const res = await Promise.race([api.get('/markets/ticker'), new Promise((_, reject) => setTimeout(() => reject(new Error('Ticker request timed out')), 10000))]);
        if (res.data.items && res.data.items.length > 0) {
          setTickerData(res.data.items);
          setError(false);
        }
      } catch (err) {
        console.error('Failed to fetch ticker:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchTicker();
    const interval = setInterval(fetchTicker, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const formatPrice = (price) => {
    if (!price) return '$0.00';
    if (price >= 1000) return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (price >= 1) return `$${price.toFixed(2)}`;
    return `$${price.toFixed(4)}`;
  };

  // Show loading state
  if (loading) {
    return (
      <div className="w-full bg-[#0D0D0D] border-b border-white/5 py-2" data-testid="market-ticker">
        <div className="flex items-center justify-center gap-2 text-gray-500">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading market data...</span>
        </div>
      </div>
    );
  }

  // Don't show if error or no data
  if (error || tickerData.length === 0) {
    return null;
  }

  // Duplicate items for seamless infinite scroll
  const duplicatedData = [...tickerData, ...tickerData];

  return (
    <div className="w-full bg-[#0D0D0D] border-b border-white/5 overflow-hidden" data-testid="market-ticker">
      <div className="ticker-wrapper">
        <div className="ticker-content flex items-center gap-8 py-2 animate-ticker">
          {duplicatedData.map((item, idx) => (
            <div 
              key={`${item.symbol}-${idx}`} 
              className="flex items-center gap-2 whitespace-nowrap px-4"
            >
              <span className="font-medium text-white">{item.symbol}</span>
              <span className="text-gray-300 font-mono">{formatPrice(item.price)}</span>
              <span className={`flex items-center gap-0.5 text-sm ${
                (item.change || 0) >= 0 ? 'text-[#00E676]' : 'text-red-400'
              }`}>
                {(item.change || 0) >= 0 ? (
                  <TrendingUp className="w-3 h-3" />
                ) : (
                  <TrendingDown className="w-3 h-3" />
                )}
                {Math.abs(item.change || 0).toFixed(2)}%
              </span>
            </div>
          ))}
        </div>
      </div>
      
      <style jsx="true">{`
        .ticker-wrapper {
          width: 100%;
          overflow: hidden;
        }
        
        .animate-ticker {
          animation: ticker 60s linear infinite;
          width: max-content;
        }
        
        @keyframes ticker {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        
        .ticker-wrapper:hover .animate-ticker {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
}
